import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const distributedDebuggingComponent: ComponentDef = {
  id: 'distributed-debugging',
  name: 'Distributed Debugging',
  description: 'Detect rank desynchronization, NaNs, and communication hangs in multi-GPU training.',
  category: 'parallelism',
  params: [
    { name: 'n_gpus', label: 'GPUs', type: 'number', default: 8, min: 2, max: 1024, step: 1 },
    {
      name: 'variant',
      label: 'View',
      type: 'select',
      default: 'common_failures',
      options: [
        { label: 'Common Failures', value: 'common_failures' },
        { label: 'NCCL Debugging', value: 'nccl_debug' },
        { label: 'Debugging Tools', value: 'debug_tools' }
      ]
    }
  ],
  buildGraph: (params) => {
    const n_gpus = Number(params.n_gpus);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('rank_0', [n_gpus]);

    if (variant === 'common_failures') {
      // Show common distributed training failures as separate branches
      g.replicate('ranks', 'rank_0', [n_gpus], n_gpus);

      // Branch 1: Rank desync (gradient mismatch)
      g.activation('desync', 'ranks', 'Rank Desync');
      g.metric('desync_err', 'desync', 'Gradient mismatch across ranks');

      // Branch 2: OOM (memory overflow)
      g.activation('oom', 'ranks', 'OOM');
      g.metric('oom_err', 'oom', 'CUDA out of memory');

      // Branch 3: Gradient explosion (NaN propagation)
      g.activation('nan', 'ranks', 'Gradient Explosion');
      g.metric('nan_err', 'nan', 'NaN propagation through AllReduce');

      // Branch 4: Deadlock (missing barrier)
      g.activation('deadlock', 'ranks', 'Deadlock');
      g.metric('deadlock_err', 'deadlock', 'Missing barrier / timeout');

      g.output('out', 'desync_err');
      g.output('out_oom', 'oom_err');
      g.output('out_nan', 'nan_err');
      g.output('out_deadlock', 'deadlock_err');
    } else if (variant === 'nccl_debug') {
      // NCCL debugging workflow: sequential steps
      g.activation('set_debug', 'rank_0', 'Set NCCL_DEBUG=INFO');
      g.activation('run_train', 'set_debug', 'Run Training');
      g.activation('capture_logs', 'run_train', 'Capture Logs');
      g.activation('analyze_timeout', 'capture_logs', 'Analyze Timeout Patterns');
      g.activation('check_conn', 'analyze_timeout', 'Check Connectivity');
      g.activation('fix', 'check_conn', 'Fix NCCL Config');
      g.metric('result', 'fix', 'NCCL debug complete');
      g.output('out', 'result');
    } else {
      // Debugging tools: each tool as a separate branch
      // Tool 1: PyTorch Profiler
      g.activation('profiler', 'rank_0', 'PyTorch Profiler');
      g.metric('profiler_out', 'profiler', 'Op-level timing & memory');

      // Tool 2: NCCL flight recorder
      g.activation('flight_rec', 'rank_0', 'NCCL Flight Recorder');
      g.metric('flight_out', 'flight_rec', 'Collective trace & hangs');

      // Tool 3: Checkpoint inspector
      g.activation('ckpt_inspect', 'rank_0', 'Checkpoint Inspector');
      g.metric('ckpt_out', 'ckpt_inspect', 'Param/optimizer state diff');

      // Tool 4: Gradient monitor
      g.activation('grad_mon', 'rank_0', 'Gradient Monitor');
      g.metric('grad_out', 'grad_mon', 'Grad norm & distribution');

      g.output('out', 'profiler_out');
      g.output('out_flight', 'flight_out');
      g.output('out_ckpt', 'ckpt_out');
      g.output('out_grad', 'grad_out');
    }

    return g.build();
  }
};
